import axios, { AxiosInstance, AxiosError, AxiosResponse } from "axios";
import { toast } from "@/lib/toast";
import AppEnv from "@/config/env"; // 환경변수 무조건 먼저 로드

// 🌐 Axios 인스턴스 생성
const apiClient: AxiosInstance = axios.create({
  baseURL: AppEnv.API_URL, // 검증된 API URL
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
});

// 🔧 토큰 갱신 재시도 카운터
let tokenRefreshRetryCount = 0;
const MAX_REFRESH_RETRY = 3;

// 🔧 토큰 관리 유틸리티
const TokenManager = {
  // 토큰 저장
  setAccessToken: (accessToken: string) => {
    localStorage.setItem("accessToken", accessToken);
  },

  // 리프레시 토큰 저장
  setRefreshToken: (refreshToken: string) => {
    localStorage.setItem("refreshToken", refreshToken);
  },

  // 토큰 조회
  getAccessToken: () => {
    return localStorage.getItem("accessToken");
  },

  // 리프레시 토큰 조회
  getRefreshToken: () => {
    return localStorage.getItem("refreshToken");
  },

  // 토큰 제거
  removeAccessToken: () => {
    localStorage.removeItem("accessToken");
  },

  // 리프레시 토큰 제거
  removeRefreshToken: () => {
    localStorage.removeItem("refreshToken");
  },

  // 재시도 카운터 초기화
  resetRetryCount: () => {
    tokenRefreshRetryCount = 0;
  },

  // 재시도 카운터 증가
  incrementRetryCount: () => {
    tokenRefreshRetryCount++;
  },

  // 재시도 횟수 확인
  canRetry: () => {
    return tokenRefreshRetryCount < MAX_REFRESH_RETRY;
  },

  // 🚨 프론트엔드 토큰 검사 (UX 개선용만 - 보안 검증 아님)
  isAccessTokenExpiredOnClient: (token: string) => {
    try {
      // JWT 토큰 디코딩 (서명 검증은 백엔드에서만 가능)
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;

      // 만료 시간 체크 (클라이언트 측 시간 기준)
      const isExpired = payload.exp <= currentTime;

      return isExpired;
    } catch (error) {
      console.error("❌ [CLIENT] 토큰 파싱 오류:", error);
      return true; // 파싱 실패 시 만료된 것으로 간주
    }
  },

  // 토큰 존재 여부 확인
  hasAccessToken: () => {
    const token = TokenManager.getAccessToken();
    return !!token;
  },

  // 🔄 토큰 자동 갱신 (앱 초기화 시 사용)
  refreshTokenIfNeeded: async () => {
    const accessToken = TokenManager.getAccessToken();
    const refreshToken = TokenManager.getRefreshToken();

    // 둘 다 없으면 로그인 필요
    if (!accessToken || !refreshToken) {
      return false;
    }

    // 액세스 토큰 만료 체크
    try {
      const isExpired = TokenManager.isAccessTokenExpiredOnClient(accessToken);

      if (isExpired) {
        // 재시도 횟수 체크
        if (!TokenManager.canRetry()) {
          console.error("❌ [TOKEN_MANAGER] 토큰 갱신 최대 재시도 횟수 초과");
          TokenManager.removeAccessToken();
          TokenManager.removeRefreshToken();
          TokenManager.resetRetryCount();
          window.location.href = "/login";
          return false;
        }

        TokenManager.incrementRetryCount();

        // 리프레시 토큰으로 새 액세스 토큰 요청
        const response = await apiClient.post("/refresh", {
          refreshToken: refreshToken,
        });

        const newAccessToken = response.data;
        TokenManager.setAccessToken(newAccessToken);
        TokenManager.resetRetryCount(); // 성공 시 카운터 초기화

        return true;
      }

      return true;
    } catch (error) {
      console.error("❌ [TOKEN_MANAGER] 토큰 갱신 실패:", error);

      // 재시도 가능한 경우 false 반환
      if (TokenManager.canRetry()) {
        return false;
      }

      // 최대 재시도 횟수 초과 시 로그인 페이지로
      TokenManager.removeAccessToken();
      TokenManager.removeRefreshToken();
      TokenManager.resetRetryCount();
      window.location.href = "/login";

      return false;
    }
  },
};

// 🔧 Request Interceptor - 요청 전에 실행
apiClient.interceptors.request.use(
  config => {
    const url = config.url || "";
    const method = config.method?.toUpperCase() || "GET";

    // JWT 토큰 확인 및 헤더 추가
    const token = TokenManager.getAccessToken();

    if (token) {
      // 토큰이 있으면 무조건 헤더에 추가 (만료 체크는 백엔드에서)
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // 토큰이 없을 때 Authorization 헤더 제거
      if (config.headers) {
        delete config.headers.Authorization;
      }
    }

    return config;
  },
  error => {
    console.error("🚨 [AXIOS] 요청 오류:", error);
    return Promise.reject(error);
  }
);

// 🔧 Response Interceptor - 응답 후에 실행
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const { status, config } = response;
    const url = config.url || "";
    const method = config.method?.toUpperCase() || "GET";

    return response;
  },
  async (error: AxiosError) => {
    const { response, config } = error;
    const status = response?.status;

    // 🔐 401 Unauthorized - 토큰 만료 → 자동 갱신 시도
    // refresh 요청은 토큰 갱신 대상에서 제외 (무한 루프 방지)
    const isRefreshRequest = config?.url?.includes("/refresh");
    if (status === 401 && !isRefreshRequest) {
      const refreshToken = TokenManager.getRefreshToken();

      if (refreshToken) {
        // 재시도 횟수 체크
        if (!TokenManager.canRetry()) {
          console.error("❌ [AXIOS] 토큰 갱신 최대 재시도 횟수 초과");
          TokenManager.removeAccessToken();
          TokenManager.removeRefreshToken();
          TokenManager.resetRetryCount();

          toast.error("세션이 만료되었습니다. 다시 로그인해주세요.");
          window.location.href = "/login";

          return Promise.reject(new Error("세션이 만료되었습니다"));
        }

        try {
          TokenManager.incrementRetryCount();

          // 🔄 새로운 액세스 토큰 요청
          const response = await apiClient.post("/refresh", {
            refreshToken: refreshToken,
          });

          const newAccessToken = response.data;

          // 새 토큰 저장
          TokenManager.setAccessToken(newAccessToken);
          TokenManager.resetRetryCount(); // 성공 시 카운터 초기화

          // 원래 요청 재시도
          if (config) {
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiClient.request(config);
          }
        } catch (refreshError) {
          console.error("❌ [AXIOS] 토큰 갱신 실패:", refreshError);

          // 재시도 가능한 경우
          if (TokenManager.canRetry()) {
            return Promise.reject(refreshError);
          }

          // 최대 재시도 횟수 초과 시 로그인 페이지로
          TokenManager.removeAccessToken();
          TokenManager.removeRefreshToken();
          TokenManager.resetRetryCount();

          toast.error("세션이 만료되었습니다. 다시 로그인해주세요.");
          window.location.href = "/login";

          return Promise.reject(new Error("세션이 만료되었습니다"));
        }
      } else {
        TokenManager.removeAccessToken();
        toast.error("세션이 만료되었습니다. 다시 로그인해주세요.");
        window.location.href = "/login";

        return Promise.reject(new Error("세션이 만료되었습니다"));
      }
    }

    // refresh 요청이 401을 받으면 바로 로그인 페이지로 이동
    if (status === 401 && isRefreshRequest) {
      console.error("❌ [AXIOS] 토큰 갱신 요청 실패 (401)");
      TokenManager.removeAccessToken();
      TokenManager.removeRefreshToken();
      TokenManager.resetRetryCount();

      toast.error("세션이 만료되었습니다. 다시 로그인해주세요.");
      window.location.href = "/login";

      return Promise.reject(new Error("세션이 만료되었습니다"));
    }

    // 🚫 403 Forbidden - 권한 없음
    if (status === 403) {
      toast.error("해당 작업을 수행할 권한이 없습니다.");
      return Promise.reject(new Error("권한이 없습니다"));
    }

    // 🔄 500 Internal Server Error
    if (status === 500) {
      toast.error("서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
      return Promise.reject(new Error("서버 오류"));
    }

    // 🌐 네트워크 오류
    if (!response) {
      toast.error("네트워크 연결을 확인해주세요.");
      return Promise.reject(new Error("네트워크 오류"));
    }

    // 🔧 기타 오류
    const errorMessage = (response?.data as any)?.message || "알 수 없는 오류가 발생했습니다";
    toast.error(errorMessage);

    return Promise.reject(error);
  }
);

// 🛠️ 편의 함수들
export const api = {
  // 📋 GET 요청
  get: <T = any>(url: string, config?: any) => apiClient.get<T>(url, config).then(res => res.data),

  // 📝 POST 요청
  post: <T = any>(url: string, data?: any, config?: any) => apiClient.post<T>(url, data, config).then(res => res.data),

  // 🔄 PUT 요청
  put: <T = any>(url: string, data?: any, config?: any) => apiClient.put<T>(url, data, config).then(res => res.data),

  // 🗑️ DELETE 요청
  delete: <T = any>(url: string, config?: any) => apiClient.delete<T>(url, config).then(res => res.data),

  // 🔍 토큰 확인 (클라이언트 측 체크)
  checkToken: () => {
    const token = TokenManager.getAccessToken();
    return token ? !TokenManager.isAccessTokenExpiredOnClient(token) : false;
  },
};

// 🔑 토큰 관리자도 export
export { TokenManager };

export default apiClient;
